import { useState } from 'react';
import { gamification, type GamPeriod } from '../lib/api';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../hooks/useAuth';
import { useTranslation, type TKey } from '../i18n';
import { EmptyState, Sidenote } from '../components/ui';

const PERIODS: GamPeriod[] = ['all', 'monthly', 'weekly', 'daily'];

const XP_RULE_ORDER = [
  'answer_correct', 'book_clear', 'stage_complete', 'cr_submit',
  'streak_day', 'question_propose', 'question_adopted', 'review_participate',
] as const;

/** Last-4 of a UUID as a readable handle (no usernames yet). */
function handle(userId: string): string {
  return `#${userId.replace(/-/g, '').slice(-4).toUpperCase()}`;
}

export default function AchievementsPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [period, setPeriod] = useState<GamPeriod>('all');

  const me = useApi(() => gamification.me(), []);
  const badges = useApi(() => gamification.badges(), []);
  const rules = useApi(() => gamification.rules(), []);
  const board = useApi(() => gamification.leaderboard(period), [period]);

  const level = me.status === 'success' ? me.data.level : null;
  const badgeList = badges.status === 'success' ? badges.data : [];
  const earnedCount = badgeList.filter((b) => b.earned).length;
  const pct = level ? Math.round(level.ratio * 100) : 0;
  const atMax = level ? level.next_level_at <= level.level_floor : false;

  return (
    <div className="container">
      {/* ---------------------------------------------------------------- Hero */}
      <section className="gam-hero">
        <div className="gam-hero__main">
          <div className="kicker">{t('gam.kicker')}</div>
          <h1 className="gam-hero__title">
            {t('gam.heroLine')} <em>{t('gam.heroEm')}</em>
          </h1>
          <p className="gam-hero__lead">{t('gam.heroSub')}</p>

          <div className="gam-xp">
            <div className="gam-xp__ring" aria-hidden="true">
              <span className="gam-xp__lvl">{level ? level.level : '—'}</span>
              <span className="gam-xp__lvl-label">{t('gam.levelLabel')}</span>
            </div>
            <div className="gam-xp__bar-block">
              <div className="gam-xp__nums">
                <span className="gam-xp__total">
                  {level ? level.total_xp.toLocaleString() : '0'}{' '}
                  <span className="gam-xp__unit">{t('gam.xpTotal')}</span>
                </span>
                <span className="gam-xp__next">
                  {atMax
                    ? t('gam.maxLevel')
                    : `${level ? level.needed.toLocaleString() : 0} ${t('gam.xpUnit')} ${t('gam.toNext')}`}
                </span>
              </div>
              <div
                className="gam-xp__bar"
                role="progressbar"
                aria-valuenow={pct}
                aria-valuemin={0}
                aria-valuemax={100}
              >
                <div className="gam-xp__fill" style={{ width: `${atMax ? 100 : pct}%` }} />
              </div>
            </div>
          </div>
        </div>

        <aside className="gam-hero__aside">
          <Sidenote label={t('gam.aside.label')} marker="✦">
            <p>{t('gam.aside.body')}</p>
          </Sidenote>
          <Sidenote label={t('gam.aside.fairLabel')} tone="mute">
            <p>{t('gam.aside.fairBody')}</p>
          </Sidenote>
        </aside>
      </section>

      {/* ---------------------------------------------------------------- Badges */}
      <section>
        <div className="sec-head">
          <h2>{t('gam.badges')}</h2>
          <span className="meta">{earnedCount}/{badgeList.length}</span>
        </div>

        {badges.status === 'loading' && (
          <div className="gam-badges" aria-hidden="true">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="gam-badge gam-badge--skeleton">
                <div className="skeleton skeleton-line w-40" />
                <div className="skeleton skeleton-line w-60 mt-8" />
              </div>
            ))}
          </div>
        )}
        {badges.status === 'success' && earnedCount === 0 && (
          <EmptyState title={t('gam.badges')} description={t('gam.noBadges')} />
        )}
        {badgeList.length > 0 && (
          <div className="gam-badges">
            {badgeList.map((b) => (
              <article
                key={b.id}
                className={`gam-badge${b.earned ? ' is-earned' : ' is-locked'}`}
              >
                <div className="gam-badge__icon" aria-hidden="true">
                  {b.earned ? '✦' : '◇'}
                </div>
                <div className="gam-badge__body">
                  <h3 className="gam-badge__name">{b.name_ko}</h3>
                  <p className="gam-badge__desc">{b.desc}</p>
                </div>
                {!b.earned && <span className="gam-badge__lock">{t('gam.locked')}</span>}
                {b.earned && b.earned_at && (
                  <span className="gam-badge__date">
                    {new Date(b.earned_at).toISOString().slice(0, 10)}
                  </span>
                )}
              </article>
            ))}
          </div>
        )}
      </section>

      {/* ---------------------------------------------------------------- Leaderboard */}
      <section>
        <div className="sec-head">
          <h2>{t('gam.leaderboard')}</h2>
          <span className="meta">{t('gam.leaderboardSub')}</span>
        </div>

        <div className="gam-period" role="tablist" aria-label={t('gam.leaderboard')}>
          {PERIODS.map((p) => (
            <button
              key={p}
              type="button"
              role="tab"
              aria-selected={period === p}
              className={`gam-period__tab${period === p ? ' is-active' : ''}`}
              onClick={() => setPeriod(p)}
            >
              {t(`gam.period.${p}` as TKey)}
            </button>
          ))}
        </div>

        {board.status === 'loading' && (
          <div className="gam-board" aria-hidden="true">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="gam-row gam-row--skeleton">
                <div className="skeleton skeleton-line w-30" />
              </div>
            ))}
          </div>
        )}
        {board.status === 'success' && board.data.rows.length === 0 && (
          <EmptyState title={t('gam.leaderboard')} description={t('gam.emptyBoard')} />
        )}
        {board.status === 'success' && board.data.rows.length > 0 && (
          <ol className="gam-board">
            {board.data.rows.map((r) => {
              const isMe = user?.id === r.user_id;
              return (
                <li
                  key={`${r.rank}-${r.user_id}`}
                  className={`gam-row${isMe ? ' is-me' : ''}${r.rank <= 3 ? ` gam-row--top${r.rank}` : ''}`}
                >
                  <span className="gam-row__rank">{r.rank}</span>
                  <span className="gam-row__who">
                    {isMe ? t('gam.you') : handle(r.user_id)}
                  </span>
                  <span className="gam-row__xp">
                    {r.value.toLocaleString()} <span className="gam-row__unit">{t('gam.xpUnit')}</span>
                  </span>
                </li>
              );
            })}
          </ol>
        )}
      </section>

      {/* ---------------------------------------------------------------- How XP works */}
      <section>
        <div className="sec-head">
          <h2>{t('gam.howXp')}</h2>
          <span className="meta">{t('gam.howXpSub')}</span>
        </div>

        <div className="gam-how">
          <ul className="gam-rules">
            {XP_RULE_ORDER.map((kind) => {
              const rule = rules.status === 'success'
                ? rules.data.xp_rules.find((r) => r.kind === kind)
                : undefined;
              if (!rule) return null;
              return (
                <li key={kind} className="gam-rule">
                  <span className="gam-rule__label">{t(`gam.xpRule.${kind}` as TKey)}</span>
                  <span className="gam-rule__xp">+{rule.xp} {t('gam.xpUnit')}</span>
                </li>
              );
            })}
          </ul>

          <div className="gam-curve">
            <div className="kicker">{t('gam.levelCurve')}</div>
            <ul className="gam-curve__list">
              {(rules.status === 'success' ? rules.data.level_curve : [])
                .slice(0, 10)
                .map((threshold, i) => (
                  <li key={i} className="gam-curve__row">
                    <span className="gam-curve__lvl">{t('gam.levelN')} {i + 1}</span>
                    <span className="gam-curve__xp">{threshold.toLocaleString()} {t('gam.xpUnit')}</span>
                  </li>
                ))}
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
}
